"use client";

import React, { useMemo, useState } from "react";
import Image from "next/image";
import { HubSpotForm } from "@/components/ui/hubspot-form";
import { routing } from "@/i18n/routing";
import { useTranslations } from "next-intl";
import { HUBSPOT_FORMS, HUBSPOT_PORTAL_ID, HUBSPOT_REGION } from "@/data/hubspot-forms";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { HighlightCard, isHighlightCollapsible, type CollapseMode } from "@/components/servicios/highlight-card";
import { CtaBand, CtaButtons, SectionHeader, SeoBlock, ServiceSection } from "@/components/servicios/service-section";

type Highlight = {
  title: string;
  description: React.ReactNode;
  icon?: React.ReactNode;
};

export type Feature = {
  title: string;
  description?: string;
  icon?: string;
};

export type Step = {
  title: string;
  description: string;
};

/** Convenience shape for service pages that pass the same list to both `highlights` and `features`. */
export type ServiceItem = {
  title: string;
  description: string;
  icon?: string;
};

type ServicePageContent = {
  locale: string;
  title: string;
  subtitle: string;
  badge?: string;
  heroImage?: string;
  heroImageAlt?: string | string[];
  featureImageAlt?: string | string[];
  galleryImageAlt?: string | string[];
  highlights: Highlight[];
  features: Feature[];
  steps: Step[];
  featuresTitle?: string;
  featuresHeadingLevel?: "h2" | "h3";
  featuresSubtitle?: string;
  galleryTitle?: string;
  galleryDescription?: string;
  galleryImages?: Array<{ src: string; alt?: string; title?: string }>;
  highlightHeadingLevel?: "h2" | "h3";
  highlightColumns?: 1 | 2 | 3;
  highlightCollapseMode?: CollapseMode;
  introSections?: Array<{
    title?: string;
    content: string;
  }>;
  customSection?: React.ReactNode;
  secondaryCtaHref?: string;
  secondaryCtaLabel?: string;
  seoHeadings?: {
    intro?: string;
    h2?: string[];
    h2Paragraphs?: string[];
    h3?: string[];
    h3Paragraphs?: string[];
    image?: string;
    imageAlt?: string;
    h3Image?: string;
    h3ImageAlt?: string;
  };
  hubspotSection?: {
    eyebrow?: string;
    title?: string;
    subtitle?: string;
    portalId?: string;
    formId?: string;
  };
  ctaAfterHighlights?: boolean;
};

const getBasePath = (locale: string) =>
  locale === routing.defaultLocale ? "" : `/${locale}`;

export const ServiceTemplate = ({
  locale,
  title,
  subtitle,
  badge,
  heroImage = "/static/home/hero-illustration.webp",
  heroImageAlt,
  featureImageAlt,
  galleryImageAlt,
  highlights,
  features,
  steps,
  featuresTitle,
  featuresHeadingLevel = "h2",
  featuresSubtitle,
  galleryTitle,
  galleryDescription,
  galleryImages = [],
  highlightHeadingLevel = "h3",
  highlightColumns = 3,
  highlightCollapseMode = "auto",
  introSections = [],
  customSection,
  secondaryCtaHref,
  secondaryCtaLabel,
  seoHeadings,
  hubspotSection,
  ctaAfterHighlights = false,
}: ServicePageContent) => {
  const t = useTranslations("serviceTemplate");
  const hubspotT = useTranslations("serviceHubspot");
  const tGallery = useTranslations("galleryPage");
  const contactHref = `${getBasePath(locale)}/contacto`;
  const formAnchorId = "service-form";
  const formAnchorHref = `#${formAnchorId}`;
  const readMoreLabel = locale.startsWith("es") ? "Leer más" : "Read more";
  const readLessLabel = locale.startsWith("es") ? "Leer menos" : "Read less";
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const preparedHighlights = useMemo(
    () =>
      highlights.map((item, index) => ({
        ...item,
        id: `${index}-${item.title}`,
        isCollapsible: isHighlightCollapsible(item.description, highlightCollapseMode),
      })),
    [highlightCollapseMode, highlights],
  );
  const resolveAlt = (alt: string | string[] | undefined, index: number, fallback: string) => {
    if (!alt) return fallback;
    if (Array.isArray(alt)) {
      if (alt.length === 0) return fallback;
      return alt[index % alt.length];
    }
    return alt;
  };
  const hubspotCopy = {
    eyebrow: hubspotSection?.eyebrow ?? hubspotT("eyebrow"),
    title: hubspotSection?.title ?? hubspotT("title"),
    subtitle: hubspotSection?.subtitle ?? hubspotT("subtitle"),
  };
  const portalId =
    hubspotSection?.portalId ?? HUBSPOT_PORTAL_ID;
  const formId =
    hubspotSection?.formId ??
    HUBSPOT_FORMS.footer;

  const basePath = getBasePath(locale);
  const ctaPrimary = { href: contactHref, label: t("cta.primary") };
  const ctaSecondary = { href: `${basePath}/estudio-gratis`, label: t("cta.secondary") };
  const hasSeoBlock = Boolean(seoHeadings?.intro || seoHeadings?.h2?.length || seoHeadings?.h3?.length);

  return (
    <div className="bg-surface-light text-ink dark:bg-surface-dark dark:text-gray-100">
      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-brand-navy text-white dark:bg-surface-dark-deep">
        <div className="absolute inset-0">
          <Image
            src={heroImage}
            alt={resolveAlt(heroImageAlt, 0, title)}
            fill
            className="object-cover opacity-80"
            priority
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/40 via-brand-navy/60 to-brand-navy dark:from-black/30 dark:via-surface-dark-deep/70 dark:to-surface-dark-deep" />
        </div>
        <div className="relative mx-auto flex max-w-6xl flex-col gap-6 px-4 pb-16 pt-28 text-center md:pb-24 md:pt-32">
          {badge && (
            <span className="mx-auto rounded-full bg-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-label text-[#f7f2ec] shadow-lg">
              {badge}
            </span>
          )}
          <h1 className="type-h1">{title}</h1>
          <p className="mx-auto max-w-3xl text-lg md:text-xl text-mist">{subtitle}</p>
          <CtaButtons
            className="gap-3"
            primary={{ href: formAnchorHref, label: t("hero.primaryCta") }}
            secondary={{
              href: secondaryCtaHref ?? `${basePath}/recomienda-y-gana`,
              label: secondaryCtaLabel ?? t("hero.secondaryCta"),
            }}
          />
        </div>
      </section>

      {hasSeoBlock && (
        <ServiceSection>
          <div className="mx-auto max-w-6xl space-y-10">
            {(seoHeadings?.intro || seoHeadings?.h2?.length) && (
              <SeoBlock
                as="h2"
                intro={seoHeadings?.intro}
                headings={seoHeadings?.h2}
                paragraphs={seoHeadings?.h2Paragraphs}
                image={seoHeadings?.image}
                imageAlt={seoHeadings?.imageAlt ?? title}
              />
            )}
            {Boolean(seoHeadings?.h3?.length) && (
              <SeoBlock
                as="h3"
                reverse
                headings={seoHeadings?.h3}
                paragraphs={seoHeadings?.h3Paragraphs}
                image={seoHeadings?.h3Image}
                imageAlt={seoHeadings?.h3ImageAlt ?? title}
              />
            )}
          </div>
        </ServiceSection>
      )}

      {/* Highlights */}
      <ServiceSection tone="plain">
        <div
          className={`mx-auto grid max-w-6xl gap-6 ${
            highlightColumns === 1
              ? "md:grid-cols-1"
              : highlightColumns === 2
                ? "md:grid-cols-2"
                : "md:grid-cols-3"
          }`}
        >
          {preparedHighlights.map((item) => (
            <HighlightCard
              key={item.id}
              title={item.title}
              description={item.description}
              icon={item.icon}
              headingLevel={highlightHeadingLevel}
              collapsible={item.isCollapsible}
              readMoreLabel={readMoreLabel}
              readLessLabel={readLessLabel}
            />
          ))}
        </div>
      </ServiceSection>

      {ctaAfterHighlights && (
        <CtaBand
          title={t("cta.title")}
          subtitle={t("cta.subtitle")}
          primary={ctaPrimary}
          secondary={ctaSecondary}
        />
      )}

      {/* Intro Sections */}
      {introSections.length > 0 && (
        <ServiceSection>
          <div className="mx-auto max-w-6xl space-y-8">
            {introSections.map((section, idx) => (
              <div key={idx} className="space-y-4">
                {section.title && (
                  <h3 className="text-2xl font-bold text-brand-navy dark:text-white">
                    {section.title}
                  </h3>
                )}
                <p className="whitespace-pre-line text-lg leading-relaxed text-gray-700 dark:text-gray-300">
                  {section.content}
                </p>
              </div>
            ))}
          </div>
        </ServiceSection>
      )}

      {customSection && <ServiceSection>{customSection}</ServiceSection>}

      {/* Features */}
      <ServiceSection>
        <div className="mx-auto grid max-w-6xl items-start gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-6">
            <div className="space-y-3">
              <p className="eyebrow">{t("features.eyebrow")}</p>
              {featuresHeadingLevel === "h3" ? (
                <h3 className="text-3xl font-black text-brand-navy dark:text-white">
                  {featuresTitle ?? t("features.title")}
                </h3>
              ) : (
                <h2 className="text-brand-navy dark:text-white type-h2">
                  {featuresTitle ?? t("features.title")}
                </h2>
              )}
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                {featuresSubtitle ?? t("features.subtitle")}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="rounded-2xl bg-surface-light p-5 shadow-card transition hover:-translate-y-0.5 hover:shadow-card dark:bg-surface-dark-raised dark:shadow-card-dark"
                >
                  {feature.icon && <div className="mb-3 text-2xl">{feature.icon}</div>}
                  <h4 className="mb-2 text-lg font-semibold text-brand-navy dark:text-white">
                    {feature.title}
                  </h4>
                  {feature.description && (
                    <p className="text-sm text-gray-700 dark:text-gray-300">{feature.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="media-frame">
            <Image
              src={heroImage}
              alt={resolveAlt(featureImageAlt ?? heroImageAlt, 1, t("features.imageAlt", { title }))}
              width={1200}
              height={900}
              className="h-full w-full object-cover"
              sizes="(min-width: 1024px) 50vw, 100vw"
              priority
            />
          </div>
        </div>
      </ServiceSection>

      {/* Steps */}
      <ServiceSection tone="soft">
        <div className="mx-auto max-w-6xl space-y-10">
          <SectionHeader title={t("steps.title")} subtitle={t("steps.subtitle")} />
          <div className="grid gap-6 md:grid-cols-3">
            {steps.map((step, index) => (
              <div
                key={step.title}
                className="flex flex-col gap-3 rounded-card bg-white p-6 text-left shadow-card dark:bg-surface-dark dark:shadow-card-dark"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-navy text-lg font-bold text-white dark:bg-brand-red">
                  {index + 1}
                </span>
                <h4 className="text-lg font-semibold text-brand-navy dark:text-white">{step.title}</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </ServiceSection>

      {/* Gallery */}
      {galleryImages.length > 0 && (
        <ServiceSection>
          <div className="mx-auto max-w-6xl space-y-8">
            <SectionHeader title={galleryTitle || t("gallery.title")} subtitle={galleryDescription} />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {galleryImages.map((image, idx) => {
                const fallbackAlt = resolveAlt(
                  galleryImageAlt,
                  idx,
                  t("gallery.imageAlt", { index: idx + 1 }),
                );
                const caption = image.title ?? fallbackAlt;
                const alt = image.alt ?? fallbackAlt;

                return (
                  <button
                    key={`${image.src}-${idx}`}
                    type="button"
                    onClick={() => setLightboxIndex(idx)}
                    className="surface-card-soft group relative aspect-[4/3] overflow-hidden text-left"
                  >
                    <Image
                      src={image.src}
                      alt={alt}
                      title={caption}
                      fill
                      className="object-cover transition duration-500 group-hover:scale-105"
                      sizes="(min-width: 1024px) 30vw, 100vw"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-transparent opacity-0 transition group-hover:opacity-100" />
                    <div className="absolute inset-x-0 bottom-0 p-4 opacity-0 transition group-hover:opacity-100">
                      <p className="text-sm font-semibold text-white">{caption}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <ImageLightbox
            images={galleryImages}
            initialIndex={lightboxIndex ?? 0}
            isOpen={lightboxIndex !== null}
            onClose={() => setLightboxIndex(null)}
            closeLabel={tGallery("lightbox.close")}
            previousLabel={tGallery("lightbox.previous")}
            nextLabel={tGallery("lightbox.next")}
            countLabel={(current, total) => tGallery("lightbox.count", { current, total })}
          />
        </ServiceSection>
      )}

      <CtaBand
        title={t("cta.title")}
        subtitle={t("cta.subtitle")}
        primary={ctaPrimary}
        secondary={ctaSecondary}
      />

      {/* HubSpot Form */}
      <ServiceSection id={formAnchorId} className="scroll-mt-24">
        <div className="surface-card-soft mx-auto max-w-5xl p-6 sm:p-8">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-label text-brand-red">
              {hubspotCopy.eyebrow}
            </p>
            <h3 className="mt-2 text-2xl sm:text-3xl font-bold text-brand-navy dark:text-white">
              {hubspotCopy.title}
            </h3>
            <p className="mt-3 text-sm sm:text-base text-gray-700 dark:text-gray-300">
              {hubspotCopy.subtitle}
            </p>
          </div>
          <div className="mt-6">
            <HubSpotForm portalId={portalId} formId={formId} region={HUBSPOT_REGION} />
          </div>
        </div>
      </ServiceSection>
    </div>
  );
};
